import axios from "axios";
import AccuracyDate from "@/models/AccuracyDate";
import HouseholdSize from "@/models/HouseholdSize";
import GeoEntity, { getGeoEntityByISO3 } from "@/models/GeoEntity";
import { Locations, updateCentroid } from "@/models/GeoLocation";
import { getTsQueryValue } from "@/utils/common";

export const FactTypes = [
  "IDPs (Stock)",
  "New Displacement (Flow)",
  "Returnees (Stock)",
  "Locally Integrated IDPs (Stock)",
  "IDPs Settled Elsewhere (Stock)",
  "People Displaced Across Borders (Stock)",
  "Multiple Displacement (Flow)",
  "Births (Flow)",
  "Deaths (Flow)",
  "Return (Flow)",
  "Failed Return / Returnee Displacement (Flow)",
  "Local Integration (Flow)",
  "Failed Local Integration (Flow)",
  "Relocation Elsewhere (Flow)",
  "Failed Relocation Elsewhere (Flow)",
  "Cross-border Flight (Flow)",
  "Cross-border Return (Flow)",
];

export interface FactDataField {
  camp?: number | null;
  term?: string | null;
  unit?: string | null;
  males?: number | null;
  rural?: number | null;
  urban?: number | null;
  figure?: number | null;
  excerpt?: string | null;
  females?: number | null;
  end_date?: any | null;
  location?: any[] | null;
  non_camp?: number | null;
  qualifier?: string | null;
  start_date?: any | null;
  stock_date?: any | null;
  reported_figure?: number | null;
  age_disaggregated_data?: any[] | null;
}

export default class Fact {
  id: number | null = null;
  type: string = "";
  name: string = "";
  displacement_type: string = "";
  serial_no: string = "";
  year: number = 0;
  iso3: string = "";
  confidential: boolean = false;
  document_id: number | null = null;
  event_id: number | null = null;
  event_role: string | null = null;
  parent_id: number | null = null;
  parent_role: string | null = null;
  groups: string[] = [];
  themes: string[] = [];
  household_size: HouseholdSize = { type: "Lookup", figure: 0 };
  comment: string = "";
  start_date: AccuracyDate | null = null;
  end_date: AccuracyDate | null = null;
  data: FactDataField = {
    camp: null,
    term: "",
    unit: "Person",
    males: null,
    rural: null,
    urban: null,
    figure: null,
    excerpt: "",
    females: null,
    end_date: { date: null, accuracy: null },
    location: [],
    non_camp: null,
    qualifier: "",
    start_date: { date: null, accuracy: null },
    stock_date: { date: null, accuracy: null },
    reported_figure: null,
    age_disaggregated_data: [],
  };
  actions: object[] | null = null;
  base_id: number | null = null;
  confidence_assessment: object[] | null = null;
  locations: Locations = { centroid: [0, 0], locations: [] };
  created_by: string | null = null;
  created_at: string | null = null;
  updated_by: string | null = null;
  updated_at: string | null = null;
  idu: boolean = false;
  gsn: string | null = null;

  // Aux entity for presentation
  event?: Event;
  fact?: Fact;
  document?: Document;
  geo_entity?: GeoEntity;

  isNew() {
    return this.id == null;
  }
}

export interface FactRecordHolder {
  parkingLot: boolean;
  id: number;
  data: any;
}

export function getFactByID(id: number) {
  return axios.get(`/psql/facts`, {
    params: {
      id: `eq.${id}`,
      select:
        "*,event(*),fact:parent_id(*),document:document_id(*),geo_entity:geo_entities(*)",
    },
    headers: { Accept: "application/vnd.pgrst.object+json" },
  });
}

export function getMasterFactByPhrase(
  phrase: string,
  iso3: string,
  displacement_type: string,
  type: string,
  year: number
) {
  return axios.get(`/psql/facts_view`, {
    params: {
      select: "id,name",
      class: "eq.Master",
      iso3: `eq.${iso3}`,
      displacement_type: `eq.${displacement_type}`,
      type: `eq.${type}`,
      year: `gte.${year - 1}`,
      name: getTsQueryValue(phrase),
      order: "created_at.desc",
    },
  });
}

export function insertFact(fact: Fact) {
  return axios.post(`/psql/facts_view`, fact, {
    headers: {
      Accept: "application/vnd.pgrst.object+json",
      Prefer: "return=representation",
    },
  });
}

export function updateFact(fact: Fact) {
  return axios.patch(`/psql/facts_view`, fact, {
    params: { id: `eq.${fact.id}` },
    headers: {
      Accept: "application/vnd.pgrst.object+json",
      Prefer: "return=representation",
    },
  });
}

export function getParkingLotFactByID(id: number) {
  return axios.get("/psql/parking_lot", {
    params: {
      id: `eq.${id}`,
      type: "eq.facts",
      status: "eq.waiting",
    },
    headers: { Accept: "application/vnd.pgrst.object+json" },
  });
}

export function convertParkingLotFactToFact(data: any) {
  let fact = new Fact();
  fact.iso3 = data.iso3;
  fact.type = data.type;
  fact.data.reported_figure = data.figure;
  fact.data.term = data.term;
  fact.data.unit = data.unit;
  fact.data.excerpt = data.excerpt;
  fact.locations = {
    centroid: [0, 0],
    locations: data.locations.map((l: any) => {
      let latlng = l.latlong.split(",");
      latlng[0] = parseFloat(latlng[0]);
      latlng[1] = parseFloat(latlng[1]);
      const { iso3, country_name, location_name, location_type } = l;
      const country_code = getGeoEntityByISO3(iso3).iso;
      return {
        name: location_name,
        type: location_type,
        moved: false,
        latlng,
        accuracy: "Point",
        geolocation: {
          iso3,
          country_code,
          country: country_name,
          name: location_name,
          lat: latlng[0],
          lon: latlng[1],
          name_suffix: location_name,
          display_name: location_name,
          alternative_names: location_name,
        },
      };
    }),
  };
  updateCentroid(fact.locations, fact.iso3);
  return fact;
}
